import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import {
    Save, Upload, Download, Plus, Trash2, FileText, Paperclip, Link2, X, Pencil, Eraser,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const newRow = (overrides = {}) => ({
    id: `row_${Math.random().toString(36).slice(2, 10)}`,
    product_id: null,
    size_id: null,
    name: "",
    size_label: "",
    sku: "",
    prices: {},
    notes: "",
    ...overrides,
});

const newColumn = (name) => ({
    id: `col_${Math.random().toString(36).slice(2, 10)}`,
    name: name || "New column",
    key: null,
});

const fileIconFor = (mime, name) => {
    const n = (name || "").toLowerCase();
    if ((mime || "").includes("pdf") || n.endsWith(".pdf")) return "PDF";
    if ((mime || "").includes("sheet") || n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv")) return "XLS";
    if ((mime || "").includes("word") || n.endsWith(".docx") || n.endsWith(".doc")) return "DOC";
    if ((mime || "").startsWith("image/")) return "IMG";
    return "FILE";
};

export default function AdminDistributorPricing() {
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [products, setProducts] = useState([]);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
    const fileRef = useRef(null);
    const xlsxRef = useRef(null);

    // For "Add from catalog" picker
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerQ, setPickerQ] = useState("");

    // For rename column
    const [renaming, setRenaming] = useState(null);
    const [renameValue, setRenameValue] = useState("");

    // Bulk-select for delete
    const [selected, setSelected] = useState(() => new Set());
    const toggleSelected = (id) => setSelected((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
    const someSelected = selected.size > 0 && !allSelected;
    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(rows.map((r) => r.id)));
        }
    };
    const deleteSelected = () => {
        const n = selected.size;
        if (!n) return;
        if (!window.confirm(`Delete ${n} selected row${n === 1 ? "" : "s"}? This will be permanent once you click Save.`)) return;
        setRows((rs) => rs.filter((r) => !selected.has(r.id)));
        setSelected(new Set());
        setDirty(true);
        toast.success(`Removed ${n} row${n === 1 ? "" : "s"} — click Save to confirm`);
    };
    const clearAllRows = () => {
        if (rows.length === 0) return;
        if (!window.confirm(`Wipe ALL ${rows.length} rows from the price list? Columns and attachments are preserved. You'll need to click Save to make it permanent.`)) return;
        setRows([]);
        setSelected(new Set());
        setDirty(true);
        toast.message("All rows cleared — click Save to confirm");
    };

    const load = async () => {
        try {
            const [pl, pr] = await Promise.all([
                api.get("/distributor-price-list"),
                api.get("/products", { params: { active_only: false } }),
            ]);
            setColumns(pl.data.columns || []);
            setRows(pl.data.rows || []);
            setAttachments(pl.data.attachments || []);
            setMeta({ updated_at: pl.data.updated_at, updated_by: pl.data.updated_by });
            setProducts(pr.data || []);
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load price list");
        }
    };
    useEffect(() => { load(); }, []);

    // ---- COLUMN ACTIONS ----
    const addColumn = () => {
        setColumns((cs) => [...cs, newColumn(`Column ${cs.length + 1}`)]);
        setDirty(true);
    };
    const removeColumn = (id) => {
        if (!window.confirm("Delete this column and all its prices? This cannot be undone until you discard.")) return;
        setColumns((cs) => cs.filter((c) => c.id !== id));
        setRows((rs) => rs.map((r) => {
            const { [id]: _gone, ...rest } = r.prices || {};
            return { ...r, prices: rest };
        }));
        setDirty(true);
    };
    const renameColumn = (id) => {
        const col = columns.find((c) => c.id === id);
        if (!col) return;
        setRenaming(col);
        setRenameValue(col.name);
    };
    const saveRename = () => {
        if (!renaming) return;
        const v = renameValue.trim() || "Column";
        setColumns((cs) => cs.map((c) => c.id === renaming.id ? { ...c, name: v } : c));
        setRenaming(null);
        setDirty(true);
    };

    // ---- ROW ACTIONS ----
    const addBlankRow = () => {
        setRows((rs) => [...rs, newRow({ name: "New medicine" })]);
        setDirty(true);
        // Scroll the table down a bit so the new row is visible (next tick)
        setTimeout(() => {
            const el = document.querySelector("[data-row-tail]");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "end" });
        }, 50);
    };
    const addFromCatalog = (variant, product) => {
        // skip if already in list
        if (rows.find((r) => r.product_id === product.id && r.size_id === variant.size_id)) {
            toast.message(`${product.name} ${variant.size_label} is already in the list`);
            return;
        }
        const distCol = columns.find((c) => c.key === "distributor");
        const mrpCol = columns.find((c) => c.key === "mrp");
        const prices = {};
        if (mrpCol) prices[mrpCol.id] = variant.price ?? 0;
        if (distCol) prices[distCol.id] = variant.distributor_price ?? variant.price ?? 0;
        const row = newRow({
            product_id: product.id,
            size_id: variant.size_id,
            name: product.name,
            size_label: variant.size_label,
            sku: variant.sku || "",
            prices,
        });
        setRows((rs) => [...rs, row]);
        setDirty(true);
    };
    const removeRow = (id) => {
        setRows((rs) => rs.filter((r) => r.id !== id));
        setSelected((s) => {
            if (!s.has(id)) return s;
            const next = new Set(s); next.delete(id); return next;
        });
        setDirty(true);
    };
    const updateRow = (id, patch) => {
        setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r));
        setDirty(true);
    };
    const updatePrice = (rowId, colId, value) => {
        const num = value === "" ? "" : parseFloat(value);
        setRows((rs) => rs.map((r) => r.id === rowId
            ? { ...r, prices: { ...(r.prices || {}), [colId]: num } }
            : r,
        ));
        setDirty(true);
    };

    // ---- SAVE ----
    const save = async () => {
        if (!columns.length) return toast.error("Add at least one column first");
        setBusy(true);
        try {
            const payload = {
                columns: columns.map(({ id, name, key }) => ({ id, name, key })),
                rows: rows.map((r) => ({
                    id: r.id,
                    product_id: r.product_id || null,
                    size_id: r.size_id || null,
                    name: r.name || "",
                    size_label: r.size_label || "",
                    sku: r.sku || "",
                    prices: Object.fromEntries(
                        Object.entries(r.prices || {})
                            .filter(([, v]) => v !== "" && v !== null && !isNaN(Number(v)))
                            .map(([k, v]) => [k, Number(v)]),
                    ),
                    notes: r.notes || "",
                })),
            };
            const { data } = await api.put("/admin/distributor-price-list", payload);
            const proj = data.projected_to_catalog || 0;
            toast.success(`Saved · ${rows.length} rows · ${proj} catalog price(s) updated`);
            setDirty(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    // ---- FILE UPLOADS ----
    const uploadAttachment = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/distributor-price-list/attachments", fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
            });
            setAttachments((a) => [...a, data]);
            toast.success(`Uploaded ${data.filename}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        }
    };
    const onAttachmentPick = (e) => {
        const files = Array.from(e.target.files || []);
        files.forEach(uploadAttachment);
        e.target.value = "";
    };
    const deleteAttachment = async (att) => {
        if (!window.confirm(`Delete attachment ${att.filename}?`)) return;
        try {
            await api.delete(`/admin/distributor-price-list/attachments/${att.id}`);
            setAttachments((a) => a.filter((x) => x.id !== att.id));
            toast.success("Deleted");
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    // ---- XLSX/CSV IMPORT — populates the spreadsheet rows + custom columns ----
    const importXlsx = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const replaceExisting = window.confirm(
            `Import "${file.name}" into the price list?\n\n` +
            `Click OK to REPLACE all existing rows with the spreadsheet contents.\n` +
            `Click Cancel to APPEND (keep existing rows + add the new ones).`,
        );
        const mode = replaceExisting ? "replace" : "append";
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(
                `/admin/distributor-price-list/import-xlsx?mode=${mode}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 },
            );
            const parts = [
                `${data.rows_imported} row${data.rows_imported === 1 ? "" : "s"} imported (${mode})`,
                data.catalog_linked > 0 ? `${data.catalog_linked} linked to catalog` : null,
                data.distributor_price_writes > 0 ? `${data.distributor_price_writes} distributor prices updated` : null,
                data.new_columns?.length ? `new columns: ${data.new_columns.join(", ")}` : null,
                data.skipped > 0 ? `${data.skipped} skipped` : null,
            ].filter(Boolean);
            toast.success(parts.join(" · "));
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        } finally { e.target.value = ""; }
    };

    // ---- "ADD FROM CATALOG" picker ----
    const catalogChoices = useMemo(() => {
        const needle = pickerQ.trim().toLowerCase();
        const out = [];
        for (const p of products) {
            for (const v of p.variants || []) {
                if (!needle
                    || p.name.toLowerCase().includes(needle)
                    || (v.size_label || "").toLowerCase().includes(needle)
                    || (v.sku || "").toLowerCase().includes(needle)
                ) {
                    out.push({ product: p, variant: v });
                }
            }
        }
        return out.slice(0, 200);
    }, [products, pickerQ]);

    return (
        <div data-testid="admin-dist-pricing">
            <Toaster position="top-center" />
            {dirty && (
                <div
                    className="sticky top-0 z-20 bg-emerald-700 text-white px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2 shadow-md"
                    data-testid="bulk-banner"
                >
                    <div className="text-sm">
                        <strong className="font-serif text-lg">Pending changes</strong>
                        <span className="ml-3 text-emerald-100">
                            You've edited the spreadsheet. Save commits every cell, row and column at once and projects linked-product prices back to the catalog.
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={load}
                            variant="outline"
                            className="rounded-none border-white text-emerald-800 bg-white hover:bg-emerald-50"
                            data-testid="bulk-reset"
                        >
                            Discard
                        </Button>
                        <Button
                            onClick={save}
                            disabled={busy}
                            className="rounded-none bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            data-testid="bulk-save"
                        >
                            <Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save all changes"}
                        </Button>
                    </div>
                </div>
            )}
            <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Wholesale · spreadsheet</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Distributor price list</h1>
                    <p className="text-sm text-stone-600 mt-2">
                        Add rows and columns freely. Link a row to a catalog product (so prices flow to the distributor portal automatically) or use free-text for items not in your catalog yet.
                    </p>
                    {meta.updated_at && (
                        <p className="text-[11px] text-stone-500 mt-2">
                            Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || "—"}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    <input ref={xlsxRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={importXlsx} data-testid="pricing-xlsx-input" />
                    <Button onClick={() => xlsxRef.current?.click()} variant="outline" className="rounded-none border-stone-400" data-testid="import-xlsx">
                        <Upload className="w-4 h-4 mr-2" />Import Excel / CSV
                    </Button>
                    <Button
                        onClick={save}
                        disabled={busy || !dirty}
                        className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 disabled:opacity-50"
                        data-testid="save-pricing"
                    >
                        <Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : (dirty ? "Save changes" : "Saved")}
                    </Button>
                </div>
            </div>

            {/* Quick-add toolbar */}
            <div className="bg-sky-50 border border-sky-100 px-4 py-3 mb-4 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-sky-800 mr-1">Add</span>
                <Button onClick={() => setPickerOpen(true)} variant="outline" size="sm" className="rounded-none border-sky-300 text-sky-700 hover:bg-sky-100" data-testid="add-from-catalog">
                    <Link2 className="w-3.5 h-3.5 mr-1" />From catalog…
                </Button>
                <Button onClick={addBlankRow} variant="outline" size="sm" className="rounded-none border-stone-300" data-testid="add-blank-row">
                    <Plus className="w-3.5 h-3.5 mr-1" />Blank row
                </Button>
                <span className="mx-2 text-stone-300">·</span>
                <Button onClick={addColumn} variant="outline" size="sm" className="rounded-none border-stone-300" data-testid="add-column">
                    <Plus className="w-3.5 h-3.5 mr-1" />Price column
                </Button>

                {/* Delete actions surface here when relevant */}
                {selected.size > 0 && (
                    <>
                        <span className="mx-2 text-stone-300">·</span>
                        <Button
                            onClick={deleteSelected}
                            size="sm"
                            className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                            data-testid="delete-selected"
                        >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete {selected.size} selected
                        </Button>
                    </>
                )}
                <Button
                    onClick={clearAllRows}
                    variant="outline"
                    size="sm"
                    disabled={rows.length === 0}
                    className="rounded-none border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed ml-1"
                    title={rows.length === 0 ? "Add some rows first to enable this" : "Wipe every row from the sheet"}
                    data-testid="clear-all-rows"
                >
                    <Eraser className="w-3.5 h-3.5 mr-1" />Clear all rows
                </Button>

                <span className="ml-auto text-[11px] text-stone-500">
                    {rows.length} row{rows.length === 1 ? "" : "s"} · {columns.length} column{columns.length === 1 ? "" : "s"}
                    {selected.size > 0 && <span className="text-red-700 ml-2 font-medium">· {selected.size} selected</span>}
                </span>
            </div>

            {/* SPREADSHEET */}
            <div className="bg-white border border-stone-200 overflow-x-auto" data-testid="pricing-grid">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-2 w-8">
                                <Checkbox
                                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                                    onCheckedChange={toggleSelectAll}
                                    aria-label="Select all rows"
                                    data-testid="select-all-rows"
                                />
                            </th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500 min-w-[180px]">Medicine</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500 min-w-[80px]">Size</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500 min-w-[100px]">SKU</th>
                            {columns.map((c) => (
                                <th key={c.id} className="text-right p-2 text-[10px] uppercase tracking-widest text-stone-500 min-w-[110px]" data-testid={`col-header-${c.id}`}>
                                    <div className="flex items-center justify-end gap-1">
                                        <span>{c.name}</span>
                                        {c.key && <span className="text-[8px] text-stone-400">·{c.key}</span>}
                                        <button onClick={() => renameColumn(c.id)} title="Rename column" className="text-stone-400 hover:text-sky-700" data-testid={`col-rename-${c.id}`}>
                                            <Pencil className="w-3 h-3" />
                                        </button>
                                        {!c.key && (
                                            <button onClick={() => removeColumn(c.id)} title="Delete column" className="text-red-500 hover:text-red-700" data-testid={`col-del-${c.id}`}>
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </th>
                            ))}
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500 min-w-[150px]">Notes</th>
                            <th className="text-right p-2 text-[10px] uppercase tracking-widest text-red-600 w-16">Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={6 + columns.length} className="p-10 text-center text-stone-500">
                                <div>Empty sheet — click <strong>"From catalog…"</strong> to add existing products, or <strong>"Blank row"</strong> for free-text medicines.</div>
                                <div className="text-[11px] text-stone-400 mt-2">Once rows are added, you'll see a red trash icon on every row and a <em>"Clear all rows"</em> button in the toolbar above.</div>
                            </td></tr>
                        ) : rows.map((r) => {
                            const linked = !!r.product_id;
                            const isSel = selected.has(r.id);
                            return (
                                <tr key={r.id} className={`border-b border-stone-100 hover:bg-sky-50/30 ${isSel ? "bg-red-50/40" : ""}`} data-testid={`row-${r.id}`}>
                                    <td className="p-2 align-top w-8 text-center">
                                        <Checkbox
                                            checked={isSel}
                                            onCheckedChange={() => toggleSelected(r.id)}
                                            aria-label={`Select ${r.name || "row"}`}
                                            data-testid={`row-select-${r.id}`}
                                        />
                                        {linked && <Link2 className="w-3 h-3 mt-1 mx-auto text-stone-400" title="Linked to catalog product" />}
                                    </td>
                                    <td className="p-1 align-top">
                                        <Input
                                            value={r.name}
                                            onChange={(e) => updateRow(r.id, { name: e.target.value })}
                                            className={`rounded-none border-stone-200 h-8 text-sm ${linked ? "bg-stone-50 text-stone-600" : ""}`}
                                            placeholder="Medicine name"
                                            readOnly={linked}
                                            data-testid={`row-name-${r.id}`}
                                        />
                                    </td>
                                    <td className="p-1 align-top">
                                        <Input
                                            value={r.size_label}
                                            onChange={(e) => updateRow(r.id, { size_label: e.target.value })}
                                            className={`rounded-none border-stone-200 h-8 text-sm font-mono ${linked ? "bg-stone-50 text-stone-600" : ""}`}
                                            placeholder="30ml"
                                            readOnly={linked}
                                            data-testid={`row-size-${r.id}`}
                                        />
                                    </td>
                                    <td className="p-1 align-top">
                                        <Input
                                            value={r.sku || ""}
                                            onChange={(e) => updateRow(r.id, { sku: e.target.value })}
                                            className="rounded-none border-stone-200 h-8 text-xs font-mono"
                                            placeholder="—"
                                            data-testid={`row-sku-${r.id}`}
                                        />
                                    </td>
                                    {columns.map((c) => (
                                        <td key={c.id} className="p-1 align-top">
                                            <Input
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                value={r.prices?.[c.id] ?? ""}
                                                onChange={(e) => updatePrice(r.id, c.id, e.target.value)}
                                                placeholder="—"
                                                className="rounded-none border-stone-200 h-8 text-right text-sm"
                                                data-testid={`cell-${r.id}-${c.id}`}
                                            />
                                        </td>
                                    ))}
                                    <td className="p-1 align-top">
                                        <Input
                                            value={r.notes || ""}
                                            onChange={(e) => updateRow(r.id, { notes: e.target.value })}
                                            className="rounded-none border-stone-200 h-8 text-xs"
                                            placeholder="—"
                                            data-testid={`row-notes-${r.id}`}
                                        />
                                    </td>
                                    <td className="p-1 align-top text-right">
                                        <button
                                            onClick={() => removeRow(r.id)}
                                            className="text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 p-1.5 transition-colors"
                                            title="Delete this row"
                                            data-testid={`row-del-${r.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr data-row-tail></tr>
                    </tbody>
                </table>
            </div>

            {/* ATTACHMENTS */}
            <div className="mt-8 bg-white border border-stone-200">
                <div className="px-4 py-3 bg-sky-50 border-b border-sky-100 flex items-center justify-between">
                    <h2 className="font-serif text-xl text-stone-900 flex items-center gap-2"><Paperclip className="w-4 h-4" />Attached files</h2>
                    <div>
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            accept=".pdf,.xlsx,.xls,.csv,.txt,.docx,.doc,.jpg,.jpeg,.png,.webp,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv,text/plain,image/*"
                            className="hidden"
                            onChange={onAttachmentPick}
                            data-testid="attach-file-input"
                        />
                        <Button onClick={() => fileRef.current?.click()} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="attach-file-btn">
                            <Upload className="w-4 h-4 mr-2" />Upload Excel / PDF / Word / Image
                        </Button>
                    </div>
                </div>
                {attachments.length === 0 ? (
                    <div className="p-8 text-center text-stone-500 text-sm">
                        No files attached yet. Upload PDFs, Excel sheets, Word docs or images — distributors will see and download them from their portal.
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100">
                        {attachments.map((a) => (
                            <div key={a.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`attachment-${a.id}`}>
                                <span className="text-[10px] font-mono px-2 py-1 bg-stone-100 text-stone-700">{fileIconFor(a.content_type, a.filename)}</span>
                                <a href={a.url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline flex-1 truncate" title={a.filename} data-testid={`attachment-link-${a.id}`}>
                                    {a.filename}
                                </a>
                                <span className="text-xs text-stone-500 whitespace-nowrap">{Math.round((a.size || 0) / 1024)} KB</span>
                                <span className="text-xs text-stone-400 whitespace-nowrap">{new Date(a.uploaded_at).toLocaleDateString()}</span>
                                <a href={a.url} download className="text-stone-500 hover:text-sky-700"><Download className="w-4 h-4" /></a>
                                <button
                                    onClick={() => deleteAttachment(a)}
                                    className="text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 p-1.5 transition-colors"
                                    title="Delete attachment"
                                    data-testid={`attachment-del-${a.id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* PICKER DIALOG — Add row from catalog */}
            <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) setPickerOpen(false); }}>
                <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Add product from catalog</DialogTitle>
                        <DialogDescription>
                            Pick a product+size. Catalog rows stay linked, and when you save, the Distributor column flows into the distributor portal automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        autoFocus
                        value={pickerQ}
                        onChange={(e) => setPickerQ(e.target.value)}
                        placeholder="Search by name, size or SKU…"
                        className="rounded-none border-stone-300 mt-2"
                        data-testid="picker-search"
                    />
                    <div className="flex-1 overflow-y-auto mt-3 border border-stone-200">
                        {catalogChoices.length === 0 ? (
                            <div className="p-8 text-center text-stone-500 text-sm">No matches.</div>
                        ) : catalogChoices.map((c) => {
                            const already = rows.find((r) => r.product_id === c.product.id && r.size_id === c.variant.size_id);
                            return (
                                <button
                                    key={`${c.product.id}-${c.variant.size_id}`}
                                    onClick={() => { addFromCatalog(c.variant, c.product); }}
                                    disabled={already}
                                    className={`w-full text-left px-3 py-2 border-b border-stone-100 hover:bg-sky-50 flex items-center gap-3 text-sm ${already ? "opacity-50 cursor-not-allowed" : ""}`}
                                    data-testid={`picker-row-${c.product.slug}-${c.variant.size_label}`}
                                >
                                    <span className="font-medium text-slate-900 flex-1 truncate">{c.product.name}</span>
                                    <span className="font-mono text-xs text-stone-600">{c.variant.size_label}</span>
                                    <span className="text-xs text-stone-500 w-24 truncate font-mono">{c.variant.sku || "—"}</span>
                                    <span className="text-xs text-stone-600 w-20 text-right">{formatINR(c.variant.price)}</span>
                                    {already && <span className="text-[10px] text-amber-700 uppercase tracking-widest">Added</span>}
                                </button>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setPickerOpen(false)}>Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* RENAME COLUMN DIALOG */}
            <Dialog open={!!renaming} onOpenChange={(o) => { if (!o) setRenaming(null); }}>
                <DialogContent className="rounded-none max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl">Rename column</DialogTitle>
                        <DialogDescription>Give this price column a meaningful name (e.g. MRP, Wholesale, Special).</DialogDescription>
                    </DialogHeader>
                    <Input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }}
                        className="rounded-none border-stone-300 mt-2"
                        data-testid="col-rename-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenaming(null)} className="rounded-none">Cancel</Button>
                        <Button onClick={saveRename} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="col-rename-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
